import React, { useEffect, useMemo, useState } from 'react';
import api from '../lib/auth';
import { formatDateTime } from '../lib/utils';

const card = "rounded-2xl border border-[#27272a] bg-[#18181b] shadow-xl";
const btn = "rounded-lg px-3 py-2 text-sm font-semibold transition";

const fmtDate = formatDateTime;

function buildLabelMap(template) {
  const map = {};
  (template?.fields || []).forEach((f) => {
    if (f?.id) map[f.id] = f.label || f.id;
  });
  return map;
}

export default function FormsRegisterPage() {
  const [submissions, setSubmissions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState('');

  const loadData = async () => {
    try {
      const [subRes, tmplRes] = await Promise.all([
        api.get('/forms/submissions'),
        api.get('/forms/templates'),
      ]);
      setSubmissions(Array.isArray(subRes.data) ? subRes.data : []);
      setTemplates(Array.isArray(tmplRes.data) ? tmplRes.data : []);
    } catch (e) {
      console.error(e);
      setMessage(e?.response?.data?.detail || 'Could not load forms register.');
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const templateById = useMemo(() => {
    const m = {};
    templates.forEach((t) => {
      const id = t.id || t._id;
      if (id) m[id] = t;
    });
    return m;
  }, [templates]);

  const selectedTemplate = selected ? templateById[selected.template_id] : null;
  const labelMap = buildLabelMap(selectedTemplate);

  const updateStatus = async (status) => {
    if (!selected) return;

    const review_notes = window.prompt('Add review notes, if required:', selected.review_notes || '');
    if (review_notes === null) return;

    try {
      const id = selected.id || selected._id;
      const res = await api.patch(`/forms/submissions/${id}/status`, {
        status,
        review_notes,
      });

      const updated = res.data;
      setSelected(prev => ({ ...prev, ...updated }));
      setSubmissions(prev => prev.map(item => {
        const itemId = item.id || item._id;
        return itemId === id ? { ...item, ...updated } : item;
      }));
      setMessage('Form status updated.');
    } catch (e) {
      console.error(e);
      setMessage(e?.response?.data?.detail || 'Could not update status.');
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] text-white p-4 md:p-6">
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Forms Register</h1>
          <p className="text-sm text-zinc-400">Review submitted operational forms, reports, assessments and checklists.</p>
        </div>
        <button onClick={loadData} className={`${btn} bg-blue-600 hover:bg-blue-500`}>
          Refresh
        </button>
      </div>

      {message ? (
        <div className="mb-4 rounded-xl border border-blue-500/30 bg-blue-500/10 px-4 py-3 text-sm text-blue-200">
          {message}
        </div>
      ) : null}

      {!selected ? (
        <div className={`${card} overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#09090b] text-zinc-400">
                <tr>
                  <th className="text-left px-4 py-3">Submitted</th>
                  <th className="text-left px-4 py-3">Form</th>
                  <th className="text-left px-4 py-3">Submitted By</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((s) => (
                  <tr key={s.id || s._id} className="border-t border-[#27272a]">
                    <td className="px-4 py-3 text-zinc-300">{fmtDate(s.created_at)}</td>
                    <td className="px-4 py-3 font-semibold">{s.template_name || 'Untitled Form'}</td>
                    <td className="px-4 py-3 text-zinc-300">{s.submitted_by_user?.name || s.submitted_by || '—'}</td>
                    <td className="px-4 py-3">
                      <span className="rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 px-2 py-1 text-xs font-semibold">
                        {s.status || 'submitted'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setSelected(s)} className={`${btn} bg-zinc-800 hover:bg-zinc-700`}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}

                {!submissions.length ? (
                  <tr>
                    <td colSpan="5" className="px-4 py-10 text-center text-zinc-500">
                      No submitted forms found.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className={`${card} p-5 max-w-4xl`}>
          <button onClick={() => setSelected(null)} className="mb-4 text-sm text-blue-400 hover:text-blue-300">
            ← Back to register
          </button>

          <div className="mb-5">
            <h2 className="text-xl font-bold">{selected.template_name || 'Submitted Form'}</h2>
            <p className="text-sm text-zinc-400">
              Submitted {fmtDate(selected.created_at)} · Status: {selected.status || 'submitted'}
            </p>
            <p className="text-xs text-zinc-500 mt-1">
              Submitted by: {selected.submitted_by_user?.name || selected.submitted_by || '—'}
            </p>
          </div>

          <div className="mb-5 flex flex-wrap gap-2">
            <button onClick={() => updateStatus('under_review')} className={`${btn} bg-yellow-600 hover:bg-yellow-500`}>
              Mark Under Review
            </button>
            <button onClick={() => updateStatus('action_required')} className={`${btn} bg-orange-600 hover:bg-orange-500`}>
              Mark Action Required
            </button>
            <button onClick={() => updateStatus('closed')} className={`${btn} bg-emerald-600 hover:bg-emerald-500`}>
              Close Report
            </button>
          </div>

          {selected.review_notes ? (
            <div className="mb-5 rounded-xl border border-yellow-500/30 bg-yellow-500/10 p-4">
              <div className="text-xs uppercase tracking-wide text-yellow-300 mb-1">Review Notes</div>
              <div className="text-sm text-yellow-100 whitespace-pre-wrap">{selected.review_notes}</div>
            </div>
          ) : null}

          <div className="space-y-3">
            {Object.entries(selected.responses || {}).map(([fieldId, value]) => (
              <div key={fieldId} className="rounded-xl border border-[#27272a] bg-[#09090b] p-4">
                <div className="text-xs uppercase tracking-wide text-zinc-500 mb-1">
                  {labelMap[fieldId] || fieldId}
                </div>
                <div className="text-sm text-zinc-100 whitespace-pre-wrap">
                  {value === true ? 'Yes' : value === false ? 'No' : String(value || '—')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
